/**
 * Busca dos dados das embarcações.
 *
 * Uma origem só: o cache publicado em `AmaView-dados`, no GitHub Pages, com
 * CORS liberado. **O navegador nunca fala com a fonte de AIS.**
 *
 * Essa é a mesma regra do CEMADEN, e aqui ela vale por dois motivos. O
 * primeiro é o de sempre: quem consome a fonte é o workflow, não os
 * visitantes, então o consumo é fixo — 4 requisições e um WebSocket por ciclo,
 * com um leitor ou com cem mil. O segundo é que a fonte (`ais.openwaters.io`,
 * projeto aiscast) é um projeto aberto em beta, sem SLA, mantido por
 * voluntários: despejar nele o tráfego do portal seria abusar de quem está
 * dando o dado de graça.
 *
 * O preço é o atraso de até um ciclo (~15 min). Para um mapa cuja imagem de
 * base já vem de 10 em 10 minutos, é coerente.
 */
import { fetchWithRetry } from '../lib/fetchRetry';
import { lonLatToMap } from '../map/projection';
import type { Anonimo, AnonimoCru, CelulaEscuta, Dados, Ficha, FichaCrua, Navio, NavioCru } from './types';

/**
 * Onde o pipeline publica (ver o repositório `AmaView-dados`).
 *
 * `VITE_NAVIOS_URL` aponta para outro lugar em desenvolvimento (por exemplo a
 * saída local do pipeline). É uma URL pública, nunca um segredo.
 */
export const BASE_DADOS =
  import.meta.env.VITE_NAVIOS_URL || 'https://helvecioneto.github.io/AmaView-dados/navios';

/**
 * Crédito genérico, usado só quando a publicação não trouxe `attribution` —
 * o que não deve acontecer. O crédito de verdade vem do dado, por fonte, e
 * **nunca é concatenado**: cada fonte do agregado tem termos próprios e o
 * aiscast é explícito ao dizer que licenças não se fundem.
 */
export const AIS_ATRIBUICAO_PADRAO = 'Open Waters AIS (https://openwaters.io/ais/)';

async function json(url: string, signal?: AbortSignal): Promise<unknown> {
  return fetchWithRetry<unknown>(url, {}, { signal, read: (r) => r.json() as Promise<unknown> });
}

/**
 * Cadastro + posições + escuta. As três descidas são paralelas.
 *
 * A escuta é opcional: se ela faltar, a camada funciona e só perde o desenho
 * da área de recepção. As posições, não — sem elas não há camada.
 */
export async function carregarDados(signal?: AbortSignal): Promise<Dados> {
  const [cruAtual, cruCadastro, cruEscuta] = await Promise.all([
    json(`${BASE_DADOS}/atual.json`, signal),
    json(`${BASE_DADOS}/cadastro.json`, signal).catch(() => null),
    json(`${BASE_DADOS}/escuta.json`, signal).catch(() => null),
  ]);

  const a = cruAtual as {
    navios?: NavioCru[];
    anonimos?: AnonimoCru[];
    attribution?: Record<string, string>;
    gerado?: string;
    trilhaHoras?: number;
  };
  if (!Array.isArray(a.navios)) throw new Error('atual.json sem a lista `navios`');

  const fichas = normalizarCadastro(cruCadastro);

  const navios: Navio[] = [];
  for (const n of a.navios) {
    if (!Number.isFinite(n.y) || !Number.isFinite(n.x) || !Number.isFinite(n.m)) continue;
    const [mx, my] = lonLatToMap(n.x, n.y);
    if (!Number.isFinite(mx) || !Number.isFinite(my)) continue;
    navios.push({
      mmsi: n.m,
      lat: n.y,
      lon: n.x,
      mx,
      my,
      sog: num(n.s),
      cog: num(n.c),
      heading: num(n.h),
      navStatus: num(n.n),
      visto: Number(n.v) || 0,
      fonte: n.f ?? null,
      sumiu: n.sumiu === true,
      // Amostra malformada (NaN, fora de ordem) faria `posicaoEm` devolver
      // distância NaN — e NaN não é "maior que a tolerância", então a
      // embarcação ficaria desenhada na coordenada do quadro anterior.
      trilha: Array.isArray(n.t)
        ? n.t.filter((a) => Array.isArray(a) && Number.isFinite(a[0]) && Number.isFinite(a[1]) && Number.isFinite(a[2]))
        : [],
      ficha: fichas.get(n.m) ?? null,
    });
  }

  const anonimos: Anonimo[] = [];
  for (const p of a.anonimos ?? []) {
    if (!Number.isFinite(p.y) || !Number.isFinite(p.x)) continue;
    const [mx, my] = lonLatToMap(p.x, p.y);
    if (!Number.isFinite(mx) || !Number.isFinite(my)) continue;
    anonimos.push({ lat: p.y, lon: p.x, mx, my, cog: num(p.c), sog: num(p.s), tipo: num(p.t) });
  }

  return {
    navios,
    anonimos,
    escuta: normalizarEscuta(cruEscuta),
    atribuicao: limparAtribuicao(a.attribution),
    gerado: a.gerado ? Date.parse(a.gerado) || null : null,
    trilhaHoras: Number(a.trilhaHoras) || 24,
  };
}

function normalizarCadastro(cru: unknown): Map<number, Ficha> {
  const saida = new Map<number, Ficha>();
  const c = cru as { navios?: Record<string, FichaCrua> } | null;
  for (const [chave, f] of Object.entries(c?.navios ?? {})) {
    const mmsi = Number(chave);
    if (!Number.isFinite(mmsi)) continue;
    saida.set(mmsi, {
      nome: texto(f.n),
      imo: num(f.i),
      indicativo: texto(f.ind),
      tipo: num(f.t),
      comprimento: num(f.comp),
      boca: num(f.larg),
      calado: num(f.cal),
      destinoCru: texto(f.dst),
      eta: f.eta && Number.isFinite(f.eta.mes) ? f.eta : null,
      fonte: texto(f.f),
      declaradoEm: num(f.d),
    });
  }
  return saida;
}

function normalizarEscuta(cru: unknown): CelulaEscuta[] {
  const e = cru as { celulaGrau?: number; celulas?: Record<string, number> } | null;
  const grau = Number(e?.celulaGrau);
  if (!Number.isFinite(grau) || grau <= 0) return [];

  const saida: CelulaEscuta[] = [];
  for (const [chave, visto] of Object.entries(e?.celulas ?? {})) {
    const [i, j] = chave.split(',').map(Number);
    if (!Number.isFinite(i) || !Number.isFinite(j)) continue;
    saida.push({
      caixa: [j! * grau, i! * grau, (j! + 1) * grau, (i! + 1) * grau],
      visto: Number(visto) || 0,
    });
  }
  return saida;
}

/**
 * Sanidade do crédito antes de ele chegar à tela.
 *
 * Diferente do CEMADEN, cujo crédito é uma constante do nosso código, este
 * texto vem da rede. Ele é renderizado como TEXTO PURO na interface (nada de
 * HTML), e aqui só garantimos que é string e que não está vazio.
 */
function limparAtribuicao(cru: unknown): Record<string, string> {
  const saida: Record<string, string> = {};
  if (!cru || typeof cru !== 'object') return saida;
  for (const [fonte, texto] of Object.entries(cru as Record<string, unknown>)) {
    if (typeof texto !== 'string') continue;
    const t = texto.trim().slice(0, 300);
    if (t) saida[fonte] = t;
  }
  return saida;
}

function num(x: unknown): number | null {
  return typeof x === 'number' && Number.isFinite(x) ? x : null;
}

function texto(x: unknown): string | null {
  if (typeof x !== 'string') return null;
  const t = x.trim();
  return t.length ? t : null;
}
